import React, { useEffect, useState } from "react";
import {
  ActivateAdmin,
  DeactivateAdmin,
  EditAdminProfile,
  GetAllAdmins,
  ViewAdminDetails,
} from "../utils/Api/ApiCalls";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { userState } from "../Services/RecoilAtoms";
import EditAccount from "./EditAccount";
import AddAdmin from "./AddAdmin";

const AdminManagement = () => {
  const currentUser = useRecoilValue(userState);
  const navigate = useNavigate();
  const [detailData, setDetailedData] = useState({});
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [load, setLoad] = useState(false);
  const [error, setError] = useState("");
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("/edit-account");

  const [editProfile, setEditProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNum: "",
    address: "",
    DateOfBirth: "",
    avatar: null,
  });

  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const RetrieveAdminProfileDetails = async () => {
      try {
        const response = await ViewAdminDetails(currentUser?.data?.info?.id);
        setDetailedData(response?.data || {});
      } catch (err) {
        setError("Failed to load admin details.");
      }
    };

    RetrieveAdminProfileDetails();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file" && files?.length > 0) {
      const avatarFile = files[0];
      const url = URL.createObjectURL(avatarFile);
      setPreviewImageUrl(url);
      setEditProfile((prevProfile) => ({
        ...prevProfile,
        [name]: avatarFile,
      }));
    } else {
      setEditProfile((prevProfile) => ({
        ...prevProfile,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editProfile.firstName || !editProfile.lastName || !editProfile.email) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoad(true);
    try {
      await EditAdminProfile(currentUser?.data?.info?.id, editProfile);
      setLoad(false);
      Swal.fire({
        icon: "success",
        title: "Profile Updated Successfully",
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        navigate("/admin-dashboard");
      });
    } catch (err) {
      setError("Failed to update profile. Please try again.");
      setLoad(false);
    }
  };

  const General = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState(null);
    const [c, setC] = useState(0);

    const fetchAdmins = async () => {
      try {
        const response = await GetAllAdmins();
        console.log(response?.data);

        setAdmins(response?.data);
      } catch (error) {
        console.error("Error fetching admins:", error);
      } finally {
        setFetching(false);
      }
    };

    useEffect(() => {
      fetchAdmins();
    }, []);

    const handleDeactivate = async (adminId) => {
      setLoading(true);
      try {
        const confirmResult = await Swal.fire({
          title: "Are you sure?",
          text: "You are about to deactivate this admin.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, deactivate!",
        });

        if (confirmResult.isConfirmed) {
          const res = await DeactivateAdmin(adminId);
          console.log("Response: ", res);
          if (res.response.status || res.status === 200 || 201) {
            fetchAdmins();
            Swal.fire({
              icon: "success",
              title: "Admin Deactivated!",
              text: "The admin has been successfully deactivated.",
            });
          }
          if (res.response.status || res.status === 401) {
            fetchAdmins();
            Swal.fire({
              icon: "error",
              title: "Unauthorized",
              text: "You do not have permissions to deactivate admin.",
            });
          }
          setLoading(false);
        }
      } catch (error) {
        console.error("Error deactivating admin:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to Deactivate",
          text: "An error occurred. Please try again.",
        });
        setLoading(false);
      }
    };

    const handleActivate = async (adminId) => {
      setLoading(true);
      try {
        const confirmResult = await Swal.fire({
          title: "Are you sure?",
          text: "You are about to activate this admin.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#28a745",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, activate!",
        });

        if (confirmResult.isConfirmed) {
          const res = await ActivateAdmin(adminId);

          if (res.response.status || res.status === 200 || 201) {
            fetchAdmins();
            Swal.fire({
              icon: "success",
              title: "Admin Activated!",
              text: "The admin has been successfully activated.",
            });
          }
          if (res.response.status || res.status === 401) {
            fetchAdmins();
            Swal.fire({
              icon: "error",
              title: "Unauthorized",
              text: "You do not have permissions to activate admin.",
            });
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("Error activating admin:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to Activate",
          text: "An error occurred. Please try again.",
        });
        setLoading(false);
      }
    };

    return (
      <div className="col-lg-9">
        <section className="content-body p-xl-4">
          {fetching ? (
            <p>Loading admins...</p>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : (
            <section>
              <h2>Admin Users</h2>
              {admins.length === 0 ? (
                <p>No admins found.</p>
              ) : (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      {/* <th>Phone</th>
                    <th>Address</th> */}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((admin, i) => (
                      <tr key={admin.id}>
                        <td>{admin.id}</td>
                        <td>{admin.fullname}</td>
                        <td>{admin.email}</td>
                        {/* <td>{admin.phone}</td>
                      <td>{admin.address}</td> */}
                        <td>
                          {admin?.isActive === 1 ? (
                            <button
                              className="btn btn-sm btn-light font-sm rounded"
                              disabled={loading}
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setC(i);
                                handleDeactivate(admin.id);
                              }}
                            >
                              {loading && c === i
                                ? "Please wait.."
                                : "Deactivate"}
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-dark font-sm rounded"
                              disabled={loading}
                              onClick={() => {
                                setC(i);
                                handleActivate(admin.id);
                              }}
                            >
                              {loading && c === i
                                ? "Please wait.."
                                : "Activate"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}
        </section>
      </div>
    );
  };

  return (
    <section className="content-main">
      <div className="content-header">
        <h2 className="content-title">Admin Management</h2>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="row gx-5">
            <aside className="col-lg-3 border-end">
              <nav className="nav nav-pills flex-lg-column mb-4">
                <div
                  className={`nav-link ${
                    activeTab === "/admin-management" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("/admin-management")}
                >
                  All Admin
                </div>
                <div
                  className={`nav-link ${
                    activeTab === "/add-admin" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("/add-admin")}
                >
                  Add Admins
                </div>
              </nav>
            </aside>
            {activeTab === "/admin-management" && <General />}
            {activeTab === "/add-admin" && <AddAdmin />}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminManagement;
